// src/app/time-attendance/components/payroll-sync/pages/payroll-unapproved-timesheets/payroll-unapproved-timesheets.component.ts

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { PayrollSyncService } from '../services/payroll-sync.service';
import {
    ApiPagedResponse,
    BookedHourApprovalDto,
    YywwDto,
} from '../models/payroll-sync.model';
import { PayrollNavComponent } from '../../../shared/payroll-nav/payroll-nav.component';

type ToastKind = 'success' | 'error' | 'info';
type ConfirmMode = 'single' | 'all';

@Component({
    selector: 'app-payroll-unapproved-timesheets',
    standalone: true,
    imports: [CommonModule, FormsModule, DatePipe, DecimalPipe, PayrollNavComponent],
    templateUrl: './payroll-unapproved-timesheets.component.html',
    styleUrls: ['./payroll-unapproved-timesheets.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayrollUnapprovedTimesheetsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    isLoading = true;
    isLoadingRows = false;
    isApprovingAll = false;
    approvingIds = new Set<number>();

    errorMessage: string | null = null;

    toastMessage: string | null = null;
    toastKind: ToastKind = 'info';
    private toastTimer: ReturnType<typeof setTimeout> | null = null;

    confirmModalOpen = false;
    confirmMode: ConfirmMode | null = null;
    rowToApprove: BookedHourApprovalDto | null = null;

    selectedYyww: YywwDto | null = null;

    yywwOpen = false;
    yywwSearch = String(new Date().getFullYear());
    yywwResults: YywwDto[] = [];
    yywwIsLoading = false;

    unapprovedRows: BookedHourApprovalDto[] = [];

    private yywwPage = 1;
    private readonly yywwPageSize = 20;
    private yywwHasMore = true;
    private yywwActiveTerm = '';
    private yywwSeq = 0;

    private readonly yywwSearch$ = new Subject<string>();

    constructor(
        private readonly payrollSyncSvc: PayrollSyncService,
        private readonly cdr: ChangeDetectorRef,
        private readonly hostEl: ElementRef<HTMLElement>,
    ) { }

    ngOnInit(): void {
        this.yywwSearch$
            .pipe(
                takeUntil(this.destroy$),
                map((value) => (value ?? '').trim()),
                debounceTime(1000),
                distinctUntilChanged(),
            )
            .subscribe((term) => {
                void this.yywwResetAndSearch(term, false);
            });

        void this.loadCore();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearToast();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(event: MouseEvent): void {
        if (!this.yywwOpen) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const host = this.hostEl.nativeElement;
        if (!host.contains(target)) {
            this.closeAllDropdowns();
            return;
        }

        const insidePanel = !!target.closest('.combo-panel');
        const insideTrigger = !!target.closest('.combo-input');

        if (!insidePanel && !insideTrigger) {
            this.closeAllDropdowns();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.confirmModalOpen) {
            this.cancelApproveConfirm();
            return;
        }

        if (this.yywwOpen) {
            this.closeAllDropdowns();
        }
    }

    async loadCore(): Promise<void> {
        this.isLoading = true;
        this.errorMessage = null;
        this.detect();

        try {
            await this.loadInitialPreviousWeek();
            await this.loadUnapprovedRows();
        } catch (error) {
            console.error(error);
            this.errorMessage = 'Failed to load unapproved timesheets.';
        } finally {
            this.isLoading = false;
            this.detect();
        }
    }

    refresh(): void {
        void this.loadUnapprovedRows();
    }

    closeAllDropdowns(): void {
        this.yywwOpen = false;
        this.detect();
    }

    openYyww(): void {
        if (this.isLoading || this.isApprovingAll) {
            return;
        }

        this.closeAllDropdowns();
        this.yywwOpen = true;
        this.detect();
    }

    onYywwSearchChange(raw: string): void {
        const value = raw ?? '';
        this.yywwSearch = value;
        this.yywwSearch$.next(value);
        this.detect();
    }

    private async loadInitialPreviousWeek(): Promise<void> {
        const currentYear = new Date().getFullYear();
        const term = String(currentYear);
        const activeSeq = ++this.yywwSeq;

        this.yywwSearch = term;
        this.yywwActiveTerm = term;
        this.yywwPage = 1;
        this.yywwHasMore = true;
        this.yywwResults = [];
        this.yywwIsLoading = true;
        this.selectedYyww = null;
        this.errorMessage = null;
        this.detect();

        try {
            const weeks = await firstValueFrom(this.payrollSyncSvc.getAllYywwsByYear(currentYear));

            if (activeSeq !== this.yywwSeq) {
                return;
            }

            this.yywwResults = this.sortWeeks(weeks);
            this.yywwHasMore = false;

            const previousWeek = this.findPreviousWeek(this.yywwResults);
            this.selectedYyww = previousWeek ?? this.yywwResults[0] ?? null;

            if (this.selectedYyww) {
                this.yywwSearch = this.selectedYyww.fullYearWeek || this.selectedYyww.yearWeek;
            }
        } finally {
            if (activeSeq === this.yywwSeq) {
                this.yywwIsLoading = false;
                this.detect();
            }
        }
    }

    private async yywwResetAndSearch(term: string, autoSelectPreviousWeek: boolean): Promise<void> {
        const activeSeq = ++this.yywwSeq;

        this.yywwActiveTerm = term;
        this.yywwPage = 1;
        this.yywwHasMore = true;
        this.yywwResults = [];
        this.yywwIsLoading = false;
        this.errorMessage = null;
        this.detect();

        if (!term) {
            this.yywwHasMore = false;
            this.selectedYyww = null;
            this.unapprovedRows = [];
            this.detect();
            return;
        }

        await this.yywwLoadNextPage(activeSeq);

        if (autoSelectPreviousWeek && activeSeq === this.yywwSeq) {
            this.autoSelectPreviousWeek();
        }
    }

    async yywwLoadNextPage(seq: number = this.yywwSeq): Promise<void> {
        if (seq !== this.yywwSeq) {
            return;
        }

        const term = this.yywwActiveTerm.trim();
        if (!term || this.yywwIsLoading || !this.yywwHasMore) {
            return;
        }

        this.yywwIsLoading = true;
        this.detect();

        try {
            const response = await firstValueFrom(
                this.payrollSyncSvc
                    .getYywws({
                        searchTerm: term,
                        pageNumber: this.yywwPage,
                        pageSize: this.yywwPageSize,
                    })
                    .pipe(
                        catchError((error) => {
                            console.error('getYywws error', error);

                            const empty: ApiPagedResponse<YywwDto[]> = {
                                status: 'error',
                                totalCount: 0,
                                totalPages: 1,
                                currentPage: 1,
                                pageSize: this.yywwPageSize,
                                data: [],
                            };

                            return of(empty);
                        }),
                    ),
            );

            if (seq !== this.yywwSeq) {
                return;
            }

            const pageData = response?.data ?? [];
            const totalPages = Number(response?.totalPages ?? 1) || 1;

            this.yywwResults = this.sortWeeks([...this.yywwResults, ...pageData]);
            this.yywwHasMore = this.yywwPage < totalPages && pageData.length > 0;

            if (this.yywwHasMore) {
                this.yywwPage += 1;
            }
        } finally {
            if (seq === this.yywwSeq) {
                this.yywwIsLoading = false;
                this.detect();
            }
        }
    }

    onYywwScroll(event: Event): void {
        const element = event.target as HTMLElement | null;
        if (!element) {
            return;
        }

        const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 120;
        if (nearBottom) {
            void this.yywwLoadNextPage();
        }
    }

    async selectWeekFromDropdown(week: YywwDto): Promise<void> {
        this.selectedYyww = week;
        this.yywwSearch = week.fullYearWeek || week.yearWeek;
        this.yywwOpen = false;
        this.unapprovedRows = [];
        this.detect();

        await this.loadUnapprovedRows();
    }

    clearSelectedWeek(): void {
        this.selectedYyww = null;
        this.yywwSearch = String(new Date().getFullYear());
        this.yywwOpen = false;
        this.unapprovedRows = [];

        void this.loadCore();
        this.detect();
    }

    async loadUnapprovedRows(): Promise<void> {
        const yywwId = this.selectedYyww?.id ?? null;

        if (!yywwId) {
            this.unapprovedRows = [];
            this.detect();
            return;
        }

        this.isLoadingRows = true;
        this.errorMessage = null;
        this.detect();

        try {
            this.unapprovedRows = await firstValueFrom(
                this.payrollSyncSvc.getUnapprovedBookedHoursByYywwId(yywwId),
            );
        } catch (error) {
            console.error('getUnapprovedBookedHoursByYywwId error', error);
            this.errorMessage = 'Failed to load unapproved timesheets.';
            this.unapprovedRows = [];
        } finally {
            this.isLoadingRows = false;
            this.detect();
        }
    }

    openApproveRowConfirm(row: BookedHourApprovalDto): void {
        if (this.isApproving(row) || this.isApprovingAll) {
            return;
        }

        this.confirmMode = 'single';
        this.rowToApprove = row;
        this.confirmModalOpen = true;
        this.detect();
    }

    openApproveAllConfirm(): void {
        if (!this.unapprovedRows.length || this.isApprovingAll || this.isLoadingRows) {
            return;
        }

        this.confirmMode = 'all';
        this.rowToApprove = null;
        this.confirmModalOpen = true;
        this.detect();
    }

    cancelApproveConfirm(): void {
        this.confirmModalOpen = false;
        this.confirmMode = null;
        this.rowToApprove = null;
        this.detect();
    }

    async confirmApprove(): Promise<void> {
        if (this.confirmMode === 'single' && this.rowToApprove) {
            const row = this.rowToApprove;
            this.cancelApproveConfirm();
            await this.approveRow(row);
            return;
        }

        if (this.confirmMode === 'all') {
            this.cancelApproveConfirm();
            await this.approveAll();
        }
    }

    private async approveRow(row: BookedHourApprovalDto): Promise<void> {
        if (this.approvingIds.has(row.id)) {
            return;
        }

        this.approvingIds.add(row.id);
        this.detect();

        try {
            await firstValueFrom(
                this.payrollSyncSvc.patchBookedHourApproval(row.id, {
                    isApproved: true,
                }),
            );

            this.unapprovedRows = this.unapprovedRows.filter((item) => item.id !== row.id);
            this.showToast('success', 'Timesheet approved.');
        } catch (error) {
            console.error('patchBookedHourApproval error', error);
            this.showToast('error', 'Failed to approve timesheet.');
        } finally {
            this.approvingIds.delete(row.id);
            this.detect();
        }
    }

    private async approveAll(): Promise<void> {
        if (!this.unapprovedRows.length || this.isApprovingAll) {
            return;
        }

        this.isApprovingAll = true;
        this.errorMessage = null;
        this.detect();

        const rows = [...this.unapprovedRows];
        let successCount = 0;
        let failCount = 0;

        try {
            for (const row of rows) {
                try {
                    this.approvingIds.add(row.id);
                    this.detect();

                    await firstValueFrom(
                        this.payrollSyncSvc.patchBookedHourApproval(row.id, {
                            isApproved: true,
                        }),
                    );

                    successCount += 1;
                    this.unapprovedRows = this.unapprovedRows.filter((item) => item.id !== row.id);
                } catch (error) {
                    console.error(`approve booked hour ${row.id} error`, error);
                    failCount += 1;
                } finally {
                    this.approvingIds.delete(row.id);
                    this.detect();
                }
            }

            if (failCount > 0) {
                this.showToast('error', `${successCount} approved. ${failCount} failed.`);
                return;
            }

            this.showToast('success', `${successCount} timesheets approved.`);
        } finally {
            this.isApprovingAll = false;
            this.detect();
        }
    }

    get selectedWeekSummary(): string {
        if (!this.selectedYyww) {
            return '—';
        }

        const fullYearWeek = this.selectedYyww.fullYearWeek ?? this.selectedYyww.yearWeek ?? '';
        const range =
            this.selectedYyww.startDate && this.selectedYyww.endDate
                ? `${this.selectedYyww.startDate} → ${this.selectedYyww.endDate}`
                : '';

        return `${fullYearWeek}${range ? ` (${range})` : ''}`.trim();
    }

    get hasRows(): boolean {
        return this.unapprovedRows.length > 0;
    }

    isApproving(row: BookedHourApprovalDto): boolean {
        return this.approvingIds.has(row.id);
    }

    getEmployeeName(row: BookedHourApprovalDto): string {
        return `${row.employee.firstName} ${row.employee.lastName}`.trim();
    }

    getApprovalStatus(row: BookedHourApprovalDto): string {
        const employeeApproved = !!row.employeeApprovedDate;
        const supervisorApproved = !!row.supervisorApprovedDate;

        if (!employeeApproved && !supervisorApproved) {
            return 'Employee + Supervisor Missing';
        }

        if (!employeeApproved) {
            return 'Employee Missing';
        }

        if (!supervisorApproved) {
            return 'Supervisor Missing';
        }

        return 'Approved';
    }

    clearToast(): void {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }

        this.toastTimer = null;
        this.toastMessage = null;
        this.detect();
    }

    private showToast(kind: ToastKind, message: string): void {
        this.toastKind = kind;
        this.toastMessage = message;

        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }

        this.toastTimer = setTimeout(() => this.clearToast(), 2600);
        this.detect();
    }

    private autoSelectPreviousWeek(): void {
        const previousWeek = this.findPreviousWeek(this.yywwResults);

        this.selectedYyww = previousWeek ?? this.yywwResults[0] ?? null;

        if (this.selectedYyww) {
            this.yywwSearch = this.selectedYyww.fullYearWeek || this.selectedYyww.yearWeek;
        }

        this.detect();
    }

    private findPreviousWeek(weeks: YywwDto[]): YywwDto | null {
        const lastWeekDate = new Date();
        lastWeekDate.setDate(lastWeekDate.getDate() - 7);
        lastWeekDate.setHours(0, 0, 0, 0);

        return (
            weeks.find((week) => {
                const startDate = new Date(`${week.startDate}T00:00:00`);
                const endDate = new Date(`${week.endDate}T23:59:59`);

                return startDate <= lastWeekDate && lastWeekDate <= endDate;
            }) ?? null
        );
    }

    private sortWeeks(weeks: YywwDto[]): YywwDto[] {
        return [...weeks].sort((a, b) => {
            if (a.year !== b.year) {
                return a.year - b.year;
            }

            return a.week - b.week;
        });
    }

    private detect(): void {
        if (!(this.cdr as unknown as { destroyed?: boolean }).destroyed) {
            this.cdr.detectChanges();
        }
    }

    trackById(_: number, item: { id: number }): number {
        return item.id;
    }
}